import { PayloadAction } from '@reduxjs/toolkit';

import { DesignState, MediaText } from 'editor/src/store/design/types';

interface ElementYUpdate {
  uuid: number;
  y: number;
}

interface RowShiftUpdate {
  /** See ReflowRowShiftUpdate in computeReflowState.ts for why groupId scopes
   *  the update — without it, two groups carrying rows with identical ids
   *  would collide. */
  groupId: string;
  rowId: string;
  shiftY: number;
}

/**
 * Applies the position part of the reflow state (the hide part flows through
 * `updateHiddenElementsAction`). Mutates `element.y` for the given uuids and
 * `row.shiftY` for the given row ids on the target spread.
 *
 * Do NOT dispatch this directly — go through `updateHiddenElementsOperation`
 * so the hide channel and the position channel stay in sync.
 */
const applyReflowStateReducer = (
  state: DesignState,
  action: PayloadAction<{
    spreadIndex: number;
    elementUpdates: ElementYUpdate[];
    rowShiftUpdates: RowShiftUpdate[];
  }>,
) => {
  if (!state.designData) {
    return;
  }
  const { spreadIndex, elementUpdates, rowShiftUpdates } = action.payload;
  const spread = state.designData.spreads[spreadIndex];
  if (!spread) {
    return;
  }

  if (elementUpdates.length) {
    const updatesByUuid = new Map<number, number>();
    elementUpdates.forEach((u) => updatesByUuid.set(u.uuid, u.y));
    spread.pages.forEach((page) => {
      page.groups.media?.forEach((element) => {
        const newY = updatesByUuid.get(element.uuid);
        if (newY !== undefined && element.type === 'text') {
          (element as MediaText).y = newY;
        }
      });
    });
  }

  if (rowShiftUpdates.length && spread.reflowGroups) {
    // Key by `${groupId}::${rowId}` so two groups holding rows with the same
    // rowId do not collide. The "::" separator is fine because row ids are
    // generated alphanumerically (no "::" in them).
    const keyOf = (groupId: string, rowId: string): string => `${groupId}::${rowId}`;
    const shiftsByCompositeKey = new Map<string, number>();
    rowShiftUpdates.forEach((u) => shiftsByCompositeKey.set(keyOf(u.groupId, u.rowId), u.shiftY));
    spread.reflowGroups.forEach((group) => {
      group.rows.forEach((row) => {
        const newShift = shiftsByCompositeKey.get(keyOf(group.id, row.id));
        if (newShift !== undefined) {
          row.shiftY = newShift === 0 ? undefined : newShift;
        }
      });
    });
  }
};

export default applyReflowStateReducer;
